import { v4 } from 'uuid';
import { RETURN_LOCATION_MODE } from '../../actions';

const COORD_EPSILON = 0.000001;

export const coordMatchByCoords = (lat, lng) => (loc) =>
  loc?.lat !== undefined &&
  Math.abs(loc.lat - lat) < COORD_EPSILON &&
  Math.abs(loc.lng - lng) < COORD_EPSILON;

export const coordMatchByOldLocation = (oldLat, oldLng) => (r) =>
  r?.location?.lat !== undefined &&
  Math.abs(r.location.lat - oldLat) < COORD_EPSILON &&
  Math.abs(r.location.lng - oldLng) < COORD_EPSILON;

export const applyJsonPudoDurations = (pudoGroups, jsonPlanGroups) => {
  if (!jsonPlanGroups || !pudoGroups.pudos.length) return pudoGroups;
  const pudoDurationMap = new Map(
    jsonPlanGroups.flatMap((g) =>
      (g.pudos || []).flatMap((p) =>
        (p.places || []).map((pl) => [p.id, pl.duration]).filter(([, d]) => d != null),
      ),
    ),
  );
  if (!pudoDurationMap.size) return pudoGroups;
  return {
    ...pudoGroups,
    pudos: pudoGroups.pudos.map((p) =>
      pudoDurationMap.has(p.id) ? { ...p, duration: pudoDurationMap.get(p.id) } : p,
    ),
  };
};

export const mergeJsonReloadDurations = (reloads, jsonReloads) => {
  if (!jsonReloads) return reloads;
  return reloads.map((r, i) =>
    jsonReloads[i]?.duration != null ? { ...r, duration: jsonReloads[i].duration } : r,
  );
};

export const updateReloadLocation = (reloads, oldCoords, newLat, newLng, newAddress) => {
  const [oldLng, oldLat] = oldCoords;
  const isMatch = coordMatchByOldLocation(oldLat, oldLng);
  let matched = false;
  return reloads.map((r) => {
    if (!matched && isMatch(r)) {
      matched = true;
      return { ...r, location: { lat: newLat, lng: newLng }, address: newAddress };
    }
    return r;
  });
};

export const updateBreakLocation = (breaks, oldCoords, newLat, newLng, newAddress) => {
  const [oldLng, oldLat] = oldCoords;
  const isMatch = coordMatchByOldLocation(oldLat, oldLng);
  return breaks.map((b) =>
    isMatch(b) ? { ...b, location: { lat: newLat, lng: newLng }, address: newAddress } : b,
  );
};

export const getPudoFilteredGroups = (filteredGroups, groups, boxOrders, orders) => {
  const pudoGroupCapacity = new Map((groups || []).map((g) => [g.id, (g.pudos || []).length]));
  const selectedPudoCount = Array.isArray(boxOrders)
    ? orders.filter((o) => boxOrders.includes(o.InternalID) && o.Activity === 'pudo').length
    : 0;
  return filteredGroups.filter((group) => {
    const capacity = pudoGroupCapacity.get(group);
    if (capacity === undefined) return true;
    if (selectedPudoCount === 0) return true;
    if (selectedPudoCount > 2) return false;
    return capacity + selectedPudoCount <= 2;
  });
};

export const addPudoToGroups = (existingGroups, pudoEntry, targetGroupId) => {
  const groupExists = existingGroups.some((g) => g.id === targetGroupId);
  return groupExists
    ? existingGroups.map((g) =>
        g.id === targetGroupId ? { ...g, pudos: [...g.pudos, pudoEntry] } : g,
      )
    : [...existingGroups, { id: targetGroupId, pudos: [pudoEntry] }];
};

export const movePudosBetweenGroups = (groups, pudoUpdates, orders) => {
  const initial = (groups || []).map((g) => ({ ...g, pudos: [...(g.pudos || [])] }));
  return pudoUpdates.reduce((newGroups, pudoOrder) => {
    const newGroupId = pudoOrder.Group;
    const originalOrder = orders.find((o) => o.InternalID === pudoOrder.InternalID);
    const oldGroupId = originalOrder?.Group;
    if (!newGroupId || oldGroupId === newGroupId) return newGroups;
    const pudoEntry = (groups || [])
      .flatMap((g) => g.pudos || [])
      .find((p) => p.id === pudoOrder.InternalID);
    if (!pudoEntry) return newGroups;
    const pruned = newGroups
      .map((g) =>
        g.id === oldGroupId
          ? { ...g, pudos: (g.pudos || []).filter((p) => p.id !== pudoOrder.InternalID) }
          : g,
      )
      .filter((g) => (g.pudos || []).length > 0);
    return addPudoToGroups(pruned, pudoEntry, newGroupId);
  }, initial);
};

export const updateDepotLocation = (location, returnLocationMode) => {
  const params = { location };
  if (returnLocationMode === RETURN_LOCATION_MODE.START) {
    params.returnLocation = location;
  }
  return params;
};

export const updateOrderLocation = (order, location, feature, isEditing) => {
  return {
    ...order,
    InternalID: !isEditing ? order.InternalID : v4(),
    Address: location.label,
    Latitude: location.value.lat,
    Longitude: location.value.lng,
    Activity: feature.properties.types[0],
    Dragged: true,
    TimeCreated: Date.now(),
  };
};

export const findOrderIndex = (orders, featureID, isPickup) => {
  return orders.findIndex(
    (order) =>
      order.InternalID === featureID && order.Activity === (isPickup ? 'pickup' : 'delivery'),
  );
};

export const handleMarkerDragEnd = ({
  marker,
  feature,
  location,
  tourPlanner,
  orders,
  solution,
  display,
  isPointDepot,
  isPointReturnLocation,
}) => {
  const actions = [];
  const isEditing = feature.properties.isEditing;
  const featureID = feature.properties.id;
  const isPickup = feature.properties.isPickup;

  actions.push({
    type: 'SELECT_TOUR',
    payload: { selectedTour: display.selectedTour, noMapMovement: true },
  });

  const latlng = marker.getLatLng();
  feature.geometry.coordinates = [latlng.lng, latlng.lat];

  if (isPointDepot(feature)) {
    const depotParams = updateDepotLocation(location, tourPlanner.returnLocationMode);
    actions.push({
      type: 'SET_TOUR_PARAMETER',
      payload: depotParams,
    });

    if (solution.json) {
      actions.push(
        { type: 'SET_SOLUTION_JSON', payload: { jsonUserChange: 'ordersDepot' } },
        { type: 'SET_SOLUTION_JSON', payload: { jsonTriggerVRP: true } },
      );
    }
    return actions;
  }

  if (isPointReturnLocation(feature)) {
    actions.push({
      type: 'SET_TOUR_PARAMETER',
      payload: { returnLocation: location },
    });

    if (solution.json) {
      actions.push(
        { type: 'SET_SOLUTION_JSON', payload: { jsonUserChange: 'ordersEndLoc' } },
        { type: 'SET_SOLUTION_JSON', payload: { jsonTriggerVRP: true } },
      );
    }
    return actions;
  }

  if (feature.properties.isPudo) {
    actions.push(
      {
        type: 'UPDATE_PUDO',
        payload: { id: featureID, lat: location.value.lat, lng: location.value.lng },
      },
      { type: 'SET_SOLUTION_JSON', payload: { jsonTriggerVRP: true } },
    );
    return actions;
  }

  const orderIndex = findOrderIndex(orders, featureID, isPickup);

  if (orderIndex >= 0) {
    const baseOrder = isEditing ? tourPlanner.editedOrder : orders[orderIndex];
    const updatedOrder = updateOrderLocation(baseOrder, location, feature, isEditing);

    const updatedOrders = [...orders];
    updatedOrders[orderIndex] = updatedOrder;

    if (!isEditing) {
      actions.push(
        { type: 'SET_ORDERS', payload: updatedOrders },
        { type: 'SET_SOLUTION_JSON', payload: { jsonTriggerVRP: true } },
      );
    } else {
      actions.push({
        type: 'SET_TOUR_PARAMETER',
        payload: {
          editedOrder: {
            ...tourPlanner.editedOrder,
            Address: location.label,
            Latitude: location.value.lat,
            Longitude: location.value.lng,
          },
        },
      });
    }
  }

  return actions;
};
